import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { SubSink } from 'subsink';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { fromEvent, of } from 'rxjs';
import { debounceTime, map, switchAll, tap } from 'rxjs/operators';
import { SmallDialogComponent } from '../../../../atoms/small-dialog/small-dialog.component';
import { stringsList } from '../../../../constants/app-config';
import { TranslateService } from '@ngx-translate/core';
import {
  PriceListDataService,
  PriceListModel,
} from '../../services/price-list-data.service';
import { PriceListAddDialogComponent } from '../price-list-add-dialog/price-list-add-dialog.component';
import { PriceListBulkImportComponent } from '../price-list-bulk-import/price-list-bulk-import.component';

@Component({
  selector: 'app-price-list-table',
  templateUrl: './price-list-table.component.html',
  styleUrls: ['./price-list-table.component.css'],
})
export class PriceListTableComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  displayedColumns: string[] = [
    'checkbox',
    'productCode',
    'barcode',
    'productName',
    'unit',
    'retailPrice',
    'actions',
  ];
  selectedInvoicesNum: number = 0;
  private subs = new SubSink();
  dataSource!: MatTableDataSource<PriceListModel>;
  searchInput: string = '';
  isLoading = false;
  isSearchFilterOn = false;
  stringsList = stringsList;
  isHovered = false;
  isHovered1 = false;
  isAdditionalDataBeingFetched = false;
  userID: string | null = null;
  private savedScrollPosition = 0;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('scrollableContainer')
  scrollableContainer!: ElementRef<HTMLElement>;

  constructor(
    public priceListDataService: PriceListDataService,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    public translate: TranslateService,
    private input: ElementRef<HTMLInputElement>
  ) {
    const obs = fromEvent(this.input.nativeElement, 'keyup').pipe(
      map((e: any) => e.target.value),
      debounceTime(250),
      tap(() => {
        this.isSearchFilterOn = true;
      }),
      map((searchInput: string) => of(searchInput?.toLowerCase())),
      switchAll()
    );
    obs.subscribe(async (data) => {
      // The stored searchKeyword has no spaces, so the query must not either.
      const keyword = (data || '').replace(/\s+/g, '');
      if (keyword.length) {
        await this.priceListDataService
          .filterItems(this.userID as string, keyword)
          .catch(console.error);
      } else {
        await this.priceListDataService
          .initializeData(this.userID as string)
          .catch(console.error);
        this.isSearchFilterOn = false;
      }
    });
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.userID = localStorage.getItem('userID');
    this.priceListDataService.initializeData(this.userID as string).then(() => {
      this.subs.sink = this.priceListDataService.items.subscribe((data) => {
        this.dataSource = new MatTableDataSource<PriceListModel>(data);
        this.dataSource.sort = this.sort;
        this.isLoading = false;
      });
    });
  }

  ngAfterViewInit(): void {
    if (this.dataSource) this.dataSource.sort = this.sort;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  async onScroll(e: any) {
    if (this.isSearchFilterOn) return;
    const container = this.scrollableContainer.nativeElement;
    const remaining =
      container.scrollHeight - (container.scrollTop + container.clientHeight);
    this.savedScrollPosition = container.scrollTop;
    if (
      remaining < container.clientHeight * 0.9 &&
      !this.isAdditionalDataBeingFetched
    ) {
      this.isAdditionalDataBeingFetched = true;
      await this.priceListDataService
        .fetchAdditional(this.userID as string)
        .then(() => {
          this.isAdditionalDataBeingFetched = false;
        });
    }
  }

  addNewItem(): void {
    const dialogRef = this.dialog.open(PriceListAddDialogComponent, {
      width: '420px',
      data: { action: 'Add' },
    });
    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        this.snackBar.open('Item successfully added', '', {
          duration: 5000,
          panelClass: ['white-snackbar'],
        });
      }
    });
  }

  openBulkImport(): void {
    const dialogRef = this.dialog.open(PriceListBulkImportComponent, {
      width: '520px',
      hasBackdrop: true,
      // The import runs in this dialog and can take a while on a large file, so
      // a stray backdrop click must not abandon it half-finished.
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe(async (imported: boolean) => {
      if (imported) {
        await this.priceListDataService
          .initializeData(this.userID as string)
          .catch(console.error);
      }
    });
  }

  editItem(item: PriceListModel): void {
    this.dialog.open(PriceListAddDialogComponent, {
      width: '420px',
      data: { action: 'Edit', data: { ...item } },
    });
  }

  toggleCheckbox(item: PriceListModel) {
    if (item.selected) {
      this.selectedInvoicesNum++;
    } else {
      this.selectedInvoicesNum--;
    }
  }

  toggleAllSelection(seed: boolean) {
    if (seed) {
      this.dataSource.data.forEach((obj) => {
        obj.selected = true;
      });
      this.selectedInvoicesNum = this.dataSource.data.length;
    } else {
      this.dataSource.data.forEach((obj) => {
        obj.selected = false;
      });
      this.selectedInvoicesNum = 0;
    }
  }

  async bulkDeleteItems() {
    const itemsForDelete = this.dataSource.data.filter((i: any) => i.selected);
    const plural = itemsForDelete.length > 1;
    const messages = plural
      ? {
          heading: 'Delete items?',
          contentPartOne:
            'Are you sure you want to delete these items from the price list?',
          action: 'Delete items',
        }
      : {
          heading: 'Delete item?',
          contentPartOne:
            'Are you sure you want to delete this item from the price list?',
          action: 'Delete item',
        };

    const dialogRef = this.dialog.open(SmallDialogComponent, {
      width: '460px',
      data: {
        heading: messages.heading,
        contentPartOne: messages.contentPartOne,
        action: messages.action,
      },
    });

    dialogRef.afterClosed().subscribe(async (response) => {
      if (response?.action === stringsList.doAction) {
        this.isLoading = true;
        const selected = this.dataSource.data.filter((i: any) => i.selected);
        await Promise.all(
          selected.map((i: any) =>
            this.priceListDataService
              .deletePriceList(i.id!)
              .catch((err) => console.log(err))
          )
        );
        this.dataSource.data = this.dataSource.data.filter(
          (i: any) => !i.selected
        );
        (this.priceListDataService as any).dataStore.items =
          this.dataSource.data;
        this.selectedInvoicesNum = 0;
        this.snackBar.open(
          plural
            ? 'Selected items have been deleted'
            : 'Selected item has been deleted',
          'Dismiss',
          { duration: 5000, panelClass: ['white-snackbar'] }
        );
        this.isLoading = false;
      }
    });
  }

  deleteItem(item: PriceListModel): void {
    const dialogRef = this.dialog.open(SmallDialogComponent, {
      width: '460px',
      data: {
        heading: 'Delete item?',
        contentPartOne: 'Are you sure you want to delete item: ',
        contentFile: item.productName,
        contentPartTwo: "You can't undo this action.",
        action: 'Delete item',
      },
    });
    dialogRef.afterClosed().subscribe((response) => {
      if (response?.action === stringsList.doAction) {
        this.priceListDataService.removeItem(item);
      }
    });
  }
}
